/* eslint-disable no-console */
import Component from "@base/js/component";

export default class TwoColumns extends Component {
  constructor() {
    super();

    this.adjustSideRail = this.adjustSideRail.bind(this);
    this.assignIdsToHeadings = this.assignIdsToHeadings.bind(this);
    this.setupSmoothScrolling = this.setupSmoothScrolling.bind(this);
    this.updateActiveLink = this.updateActiveLink.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);

    // Flag to track if scrolling is triggered by a click
    this.isClickScrolling = false;
  }

  // Defines the element selector which will initialize this component
  static selector = ".two-columns";

  // Will be called upon component instantiation
  afterInit() {
    window.addEventListener("load", () => {
      this.assignIdsToHeadings();
      this.setupSmoothScrolling();
      this.setupNavigationObserver();
    });

    this.adjustSideRail();
  }

  // Method to handle assigning unique IDs to headings
  assignIdsToHeadings() {
    const navigationLinks = document.querySelectorAll(
      ".two-columns__sticky-navigation a"
    );
    const headings = Array.from(
      document.querySelectorAll("h2.section-heading")
    );

    const headingOccurrences = new Map();

    navigationLinks.forEach((link) => {
      const targetId = link.getAttribute("href")?.replace("#", "") || "";
      const headingText = link.textContent.trim();

      // Find all matching headings with the same text
      const matchingHeadings = headings.filter(
        (heading) => heading.textContent.trim() === headingText
      );

      if (matchingHeadings.length > 0) {
        const occurrence = headingOccurrences.get(headingText) || 0;

        if (matchingHeadings[occurrence]) {
          // Assign the unique ID to the correct heading
          matchingHeadings[occurrence].setAttribute("id", targetId);

          // Increment the occurrence counter for this heading text
          headingOccurrences.set(headingText, occurrence + 1);
        }
      }
    });
  }

  // Method to implement smooth scrolling when clicking navigation links
  setupSmoothScrolling() {
    const navigationLinks = document.querySelectorAll(
      ".two-columns__sticky-navigation a"
    );

    navigationLinks.forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        const targetId = link.getAttribute("href")?.replace("#", "");
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          // Set flag to indicate scrolling is triggered by a click
          this.isClickScrolling = true;

          // Remove 'active' class from all navigation links
          navigationLinks.forEach((navLink) => {
            navLink.classList.remove("active");
          });

          // Add 'active' class to the clicked link
          link.classList.add("active");

          // Smooth scroll to the target element
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });

          // Reset flag and update active link after scrolling completes
          setTimeout(() => {
            this.isClickScrolling = false;
            this.updateActiveLink();
          }, 1000);
        }
      });
    });
  }

  // Sets up the scroll event listener with debounced updates
  setupNavigationObserver() {
    this.toggleBtn = this.element.querySelector(".two-columns__toggle-btn");
    this.stickyContainer = this.element.querySelector(
      ".two-columns__sticky-container"
    );

    if (this.toggleBtn) {
      this.toggleBtn.removeEventListener("click", this.handleToggleClick);
      this.toggleBtn.addEventListener("click", this.handleToggleClick);
    }

    this.navigationLinks = document.querySelectorAll(
      ".two-columns__sticky-navigation a"
    );
    window.addEventListener(
      "scroll",
      this.debounce(this.updateActiveLink.bind(this), 100)
    );
    this.updateActiveLink();
  }

  handleToggleClick(event) {
    event.preventDefault();
    if (this.stickyContainer) {
      this.stickyContainer.classList.toggle("expanded");
      const useElement = this.toggleBtn.querySelector("use");

      if (this.stickyContainer.classList.contains("expanded")) {
        useElement.setAttribute("xlink:href", "#close-thick");
      } else {
        useElement.setAttribute("xlink:href", "#add");
      }
    }
  }

  // Updates the active navigation link based on scroll position
  updateActiveLink() {
    // Skip updates if scrolling is triggered by a click
    if (this.isClickScrolling) return;

    const headings = Array.from(
      document.querySelectorAll("h2.section-heading")
    );
    if (headings.length === 0) return;

    // Default to the last heading's ID if no earlier condition is met
    let currentSection = headings[headings.length - 1].getAttribute("id");

    // Iterate through headings to find the active section
    for (let i = 0; i < headings.length; i++) {
      const heading = headings[i];
      const nextHeading = headings[i + 1];
      const elementPosition = heading.getBoundingClientRect().top;

      // Calculate distance to the next heading, if it exists
      const distance = nextHeading
        ? this.calculateDistance(heading, nextHeading)
        : 0;

      if (
        (nextHeading && elementPosition + distance >= 0) ||
        (!nextHeading && elementPosition >= 0)
      ) {
        currentSection = heading.getAttribute("id");
        break;
      }
    }

    this.navigationLinks.forEach((link) => {
      link.classList.remove("active");
      const href = link.getAttribute("href")?.replace("#", "");
      if (href === currentSection) link.classList.add("active");
    });
  }

  calculateDistance(currentHeading, nextHeading) {
    const rect1 = currentHeading.getBoundingClientRect();
    const rect2 = nextHeading.getBoundingClientRect();

    const centerX1 = rect1.left + rect1.width / 2;
    const centerY1 = rect1.top + rect1.height / 2;
    const centerX2 = rect2.left + rect2.width / 2;
    const centerY2 = rect2.top + rect2.height / 2;

    const totalDistance = Math.sqrt(
      (centerX2 - centerX1) ** 2 + (centerY2 - centerY1) ** 2
    );
    return totalDistance - rect1.height;
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func(...args);
      }, wait);
    };
  }

  adjustSideRail() {
    const doAdjustment = () => {
      const firstArticleBody = document.querySelector(
        ".two-columns__body .article-body"
      );

      if (firstArticleBody) {
        const firstComponent = firstArticleBody.querySelector("*");
        const sideRail = document.querySelector(
          ".two-columns .two-columns__siderail"
        );

        if (
          firstComponent &&
          sideRail &&
          window.innerWidth >= 768 &&
          ((firstComponent.classList.contains("article-body__image") &&
            firstComponent.classList.contains("full-width")) ||
            (firstComponent.classList.contains("article-body__pull-quote") &&
              firstComponent.classList.contains("centered")))
        ) {
          const offset = firstComponent.getBoundingClientRect().height + 40; // margin 40
          sideRail.style.marginTop = `${offset}px`;
        }
      }
    };

    doAdjustment();

    window.addEventListener("resize", doAdjustment, false);
  }
}

// Remove this line if this component is not intended
// to initialize on DOM load.
TwoColumns.init();
